import { useState } from "react";
import { CheckCircle2, Loader2, Upload, FileCheck2, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";

type Stage = "idle" | "checking" | "done";

const ORDS = ["VK ОРД", "Ozon ОРД"] as const;

function randomErid() {
  const c = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  return "2Vtzq" + Array.from({ length: 6 }, () => c[Math.floor(Math.random() * c.length)]).join("");
}

/** Интерактивный демо-мокап кабинета: имитация выдачи ERID (без запросов к серверу). */
export function HeroEridMockup() {
  const [ord, setOrd] = useState<(typeof ORDS)[number]>("VK ОРД");
  const [stage, setStage] = useState<Stage>("idle");
  const [erid, setErid] = useState("");
  const [copied, setCopied] = useState(false);

  const run = () => {
    setStage("checking");
    setCopied(false);
    window.setTimeout(() => {
      setErid(randomErid());
      setStage("done");
    }, 1200);
  };

  return (
    <div className="relative animate-fade-in" style={{ animationDelay: "0.15s", animationFillMode: "backwards" }}>
      <div aria-hidden className="absolute -inset-6 rounded-[2rem] bg-accent/10 blur-3xl" />
      <div className="glass-tech relative overflow-hidden shadow-[0_0_40px_hsl(var(--accent)/0.15)]">
        <div className="flex items-center justify-between border-b border-foreground/10 px-5 py-3">
          <div className="flex gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-muted" />
            <span className="h-2.5 w-2.5 rounded-full bg-muted" />
            <span className="h-2.5 w-2.5 rounded-full bg-muted" />
          </div>
          <span className="font-mono text-[11px] text-muted-foreground">eridonai.ru / кабинет</span>
        </div>

        <div className="space-y-5 p-5 sm:p-6">
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">Оператор рекламных данных</p>
            <div role="radiogroup" aria-label="Выбор ОРД" className="grid grid-cols-2 gap-2">
              {ORDS.map((o) => (
                <button
                  key={o}
                  role="radio"
                  aria-checked={ord === o}
                  onClick={() => { setOrd(o); setStage("idle"); }}
                  className={`rounded-xl border px-3 py-2 text-sm font-medium transition-colors duration-200 ${
                    ord === o ? "border-accent/60 bg-accent/10 text-foreground" : "border-foreground/10 text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {o}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 rounded-xl border border-dashed border-foreground/15 bg-background/40 p-4">
            <Upload className="h-5 w-5 shrink-0 text-primary" />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foreground">креатив_осень.jpg</p>
              <p className="text-xs text-muted-foreground">Пост в Telegram · 1080×1080</p>
            </div>
          </div>

          <div className="space-y-2 font-mono text-[13px]">
            {[
              ["Проверка ФЗ-168", stage !== "idle"],
              ["Регистрация креатива", stage === "done"],
              ["Черновик отчёта ЕРИР", stage === "done"],
            ].map(([label, ok]) => (
              <div key={label as string} className="flex items-center justify-between">
                <span className="text-muted-foreground">{label}</span>
                {ok ? (
                  <CheckCircle2 className="h-4 w-4 text-success" aria-label="готово" />
                ) : stage === "checking" ? (
                  <Loader2 className="h-4 w-4 animate-spin text-accent" aria-label="в процессе" />
                ) : (
                  <span className="text-muted-foreground/50">—</span>
                )}
              </div>
            ))}
          </div>

          {stage === "done" ? (
            <div className="flex items-center justify-between gap-3 rounded-xl border border-success/40 bg-success/10 px-4 py-3">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">ERID · {ord} · демо</p>
                <p className="truncate font-mono text-base font-semibold text-foreground">{erid}</p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                aria-label="Скопировать ERID"
                onClick={() => { navigator.clipboard?.writeText(erid); setCopied(true); }}
              >
                {copied ? <FileCheck2 className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          ) : (
            <Button variant="gradient" className="h-11 w-full" onClick={run} disabled={stage === "checking"}>
              {stage === "checking" ? "Проверяем…" : "Получить ERID"}
            </Button>
          )}
          <p className="text-center text-[11px] text-muted-foreground">Демонстрация интерфейса — реальный ERID выдаётся после входа</p>
        </div>
      </div>
    </div>
  );
}
